// src/components/AddLeadScreen.jsx
// Novo/Editar Lead premium.
// Rotas:
// /leads/new
// /leads/edit   => recebe lead via location.state.lead
// Endpoint:
// POST /psy/lead?id=ID_USUARIO
// PUT  /psy/lead/:leadId?id=ID_USUARIO
// Token refresh via header x-new-token.
// UI isolada em .al-* para não mexer em .dash-* global.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  CircleDot,
  Clock3,
  LoaderCircle,
  Menu,
  Save,
  Target,
  UserRound,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import { useToast } from "../context/ToastContext";
import "../sass/pages/add-lead.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const ORIGIN_OPTIONS = ["WhatsApp", "Instagram", "Facebook", "Google Ads", "Indicação", "Outros"];
const STATUS_OPTIONS = ["Fechado", "Não Fechado"];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function writeToken(token) {
  if (!token) return;

  try {
    sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
    sessionStorage.setItem("token", token);
    localStorage.setItem(AUTH_KEYS.TOKEN, token);
  } catch {
    // storage indisponível
  }
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}


function normalizeStatus(value = "") {
  const status = String(value || "").trim();

  if (!status) return "Não Fechado";

  if (status.toLowerCase().includes("fechado") && !status.toLowerCase().includes("não")) {
    return "Fechado";
  }

  return "Não Fechado";
}

function resolveLeadId(lead = {}) {
  return lead?._id || lead?.id || lead?.leadId || "";
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.detail || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function FieldError({ children }) {
  if (!children) return null;

  return <div className="al-field-error">{children}</div>;
}

export default function AddLeadScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const pageRef = useRef(null);

  const leadData = location?.state?.lead || null;
  const isEdit = Boolean(resolveLeadId(leadData));

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [nome, setNome] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [origemSelecionada, setOrigemSelecionada] = useState("WhatsApp");
  const [statusSelecionado, setStatusSelecionado] = useState("Não Fechado");

  const [loading, setLoading] = useState(false);

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  useEffect(() => {
    if (!leadData) return;

    setNome(String(leadData?.nome ?? leadData?.name ?? ""));
    setObservacoes(String(leadData?.observacoes ?? leadData?.observacao ?? ""));
    setOrigemSelecionada(String(leadData?.origem ?? "WhatsApp"));
    setStatusSelecionado(normalizeStatus(leadData?.status ?? "Não Fechado"));
  }, [leadData]);

  const nomeTrim = String(nome || "").trim();
  const obsTrim = String(observacoes || "").trim();

  const errors = useMemo(() => {
    const next = {};

    if (!nomeTrim) {
      next.nome = "Informe o nome do lead.";
    }

    if (nomeTrim.length > 120) {
      next.nome = "O nome pode ter no máximo 120 caracteres.";
    }

    if (obsTrim.length > 600) {
      next.observacoes = "As observações podem ter no máximo 600 caracteres.";
    }

    return next;
  }, [nomeTrim, obsTrim]);

  const hasErrors = Object.keys(errors).length > 0;

  const focusFirstError = () => {
    window.setTimeout(() => {
      const root = pageRef.current || document;
      const errorElement = root.querySelector(".al-field-error");
      const target =
        errorElement?.closest("label, .al-form-card, .al-panel, section") ||
        errorElement;

      if (!target) return;

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      const focusable = target.querySelector?.(
        "input, textarea, select, button:not([disabled])"
      );

      if (focusable && typeof focusable.focus === "function") {
        focusable.focus({ preventScroll: true });
      }
    }, 80);
  };

  const heroStatus = statusSelecionado === "Fechado" ? "converted" : "pending";

  function goBack() {
    navigate("/leads");
  }

  async function salvarLead() {
    if (hasErrors) {
      focusFirstError();
      return;
    }

    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!userId) {
      toast.error({
        title: "Sessão inválida",
        message:
          "ID do usuário não encontrado. Faça login novamente.",
        duration: 6500,
      });
      return;
    }

    if (!token) {
      toast.error({
        title: "Sessão encerrada",
        message:
          "Token não encontrado. Faça login novamente.",
        duration: 6500,
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        nome: nomeTrim,
        origem: origemSelecionada,
        observacoes: obsTrim,
        status: statusSelecionado,
      };

      const leadId = resolveLeadId(leadData);

      const url = isEdit
        ? `${API_BASE}/psy/lead/${encodeURIComponent(leadId)}?id=${encodeURIComponent(userId)}`
        : `${API_BASE}/psy/lead?id=${encodeURIComponent(userId)}`;

      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      writeToken(response.headers.get("x-new-token"));

      if (response.status === 401 || response.status === 403) {
        toast.error({
          title: "Sessão encerrada",
          message:
            "Sua sessão expirou. Faça login novamente.",
          duration: 6500,
        });
        return;
      }

      if (!response.ok) {
        const message = await readApiError(response);

        toast.error({
          title: isEdit
            ? "Lead não atualizado"
            : "Lead não cadastrado",
          message:
            message ||
            "Não foi possível salvar o lead.",
        });

        return;
      }

      toast.success({
        title: isEdit
          ? "Lead atualizado"
          : "Lead cadastrado",
        message: isEdit
          ? "As informações do lead foram atualizadas com sucesso."
          : "O novo lead foi adicionado ao funil comercial.",
      });

      navigate("/leads", {
        replace: true,
        state: { refreshed: true },
      });
    } catch (error) {
      console.error("Erro ao salvar lead:", error);

      toast.error({
        title: "Falha de comunicação",
        message:
          "Erro de conexão ao salvar lead.",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="al-page" ref={pageRef}>
      <div className="al-bg" aria-hidden="true">
        <span className="al-bg__orb al-bg__orb--one" />
        <span className="al-bg__orb al-bg__orb--two" />
        <span className="al-bg__orb al-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="al-topbar">
        <button
          className="al-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="al-brand">
          <img src={Logo} alt="PsyAssist" className="al-brand__logo" />
          <div className="al-brand__text">
            <strong>{isEdit ? "Editar Lead" : "Novo Lead"}</strong>
            <span>Cadastro • Marketing • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="al-top-actions">
          <button
            className="al-btn al-btn--ghost al-btn--desktop"
            type="button"
            onClick={goBack}
            disabled={loading}
          >
            <ArrowLeft size={16} />
            Leads
          </button>

          <button
            className="al-btn al-btn--primary"
            type="button"
            onClick={salvarLead}
            disabled={loading}
          >
            {loading ? (
              <>
                <LoaderCircle size={16} className="al-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} />
                Salvar
              </>
            )}
          </button>
        </div>
      </header>

      <main className="al-main">
        <section className="al-hero">
          <div>
            <span className="al-kicker">
              <Target size={14} aria-hidden="true" />
              {isEdit ? "Atualização de lead" : "Novo contato"}
            </span>
            <h1>{isEdit ? "Editar lead" : "Cadastrar lead"}</h1>
            <p>
              {firstName ? `${firstName}, ` : ""}
              registre a origem, status e observações para acompanhar melhor o funil comercial.
            </p>
          </div>

          <aside className={`al-hero-card al-hero-card--${heroStatus}`}>
            <div className="al-hero-card__icon" aria-hidden="true">
              {statusSelecionado === "Fechado" ? (
                <CheckCircle2 size={22} />
              ) : (
                <Clock3 size={22} />
              )}
            </div>

            <div>
              <small>Status atual</small>
              <strong>{statusSelecionado}</strong>
              <span>
                Origem: {origemSelecionada}
                {nomeTrim ? ` • ${nomeTrim}` : ""}
              </span>
            </div>
          </aside>
        </section>

        <section className="al-panel">
          <div className="al-panel__head">
            <div>
              <span>Dados do Lead</span>
              <h2>Informações principais</h2>
              <p>Preencha os campos abaixo para salvar o lead.</p>
            </div>

            <div className="al-mode">{isEdit ? "Edição" : "Novo"}</div>
          </div>

          <div className="al-form">
            <label className="al-field al-field--full">
              <span className="al-field__lbl">Nome</span>

              <div className={`al-inputwrap ${errors.nome ? "is-invalid" : ""}`}>
                <span className="al-ico" aria-hidden="true">
                  <UserRound size={18} />
                </span>

                <input
                  className="al-input"
                  value={nome}
                  onChange={(event) => {
                    setNome(event.target.value);
                  }}
                  placeholder="Nome do lead"
                  autoComplete="off"
                  maxLength={120}
                  disabled={loading}
                />
              </div>

              <FieldError>{errors.nome}</FieldError>
            </label>

            <label className="al-field">
              <span className="al-field__lbl">Origem</span>

              <select
                className="al-select"
                value={origemSelecionada}
                onChange={(event) => {
                  setOrigemSelecionada(event.target.value);
                }}
                disabled={loading}
              >
                {ORIGIN_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className="al-field">
              <span className="al-field__lbl">Status</span>

              <select
                className="al-select"
                value={statusSelecionado}
                onChange={(event) => {
                  setStatusSelecionado(event.target.value);
                }}
                disabled={loading}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className="al-field al-field--full">
              <span className="al-field__lbl">Observações</span>

              <textarea
                className={`al-textarea ${errors.observacoes ? "is-invalid" : ""}`}
                value={observacoes}
                onChange={(event) => {
                  setObservacoes(event.target.value);
                }}
                rows={5}
                placeholder="Exemplo: veio pelo Instagram, pediu valores, prefere atendimento online..."
                maxLength={600}
                disabled={loading}
              />

              <div className="al-field-row">
                <FieldError>{errors.observacoes}</FieldError>
                <span className="al-counter">{obsTrim.length}/600</span>
              </div>
            </label>
          </div>
        </section>

        <section className="al-actions" aria-label="Ações do lead">
          <button
            className="al-btn al-btn--ghost-dark"
            type="button"
            onClick={goBack}
            disabled={loading}
          >
            <ArrowLeft size={16} />
            Cancelar
          </button>

          <button
            className="al-btn al-btn--primary"
            type="button"
            onClick={salvarLead}
            disabled={loading}
          >
            {loading ? (
              <>
                <LoaderCircle size={16} className="al-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} />
                {isEdit ? "Atualizar lead" : "Salvar lead"}
              </>
            )}
          </button>
        </section>

        <div className="al-hint">
          <CircleDot size={13} />
          {isEdit
            ? "As alterações serão refletidas na lista e nos indicadores de leads."
            : "Após salvar, o lead ficará disponível na lista e no relatório de captação."}
        </div>
      </main>
    </div>
  );
}
